/*
  ITGC Request modal — application editor (Basic Details only).

  Creates and edits an access request: target user (locked once lines exist),
  title, justification, and the grant/revoke lines. Save as Draft or Submit for
  approval. Approval and fulfilment live in their own screens/agent and are NOT
  part of this modal.
*/
import React, { useCallback, useEffect, useState } from 'react';
import { Plus, CornerUpLeft, Ban, Send, Save, Check } from 'lucide-react';
import apiClient from '../../services/apiService';
import { isPickableUser } from '../../utils/userPickable';
import {
  itgcService, AccessRequest, AccessLine, AccessLineCreatePayload, RequestStatus,
} from '../../services/itgcService';
import ITGCLineModal, { LineDraft, targetLabelFor } from './ITGCLineModal';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import Badge from '../common/Badge';
import StatusSwitch from '../common/StatusSwitch';
import { statusVariant } from '../../utils/statusVariant';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { swalConfirm } from '../common/confirm';
import extractApiError from '../../utils/apiError';

// Always coerce to a string. The backend's shared validation engine returns
// detail as an array/object ([{ field, code, message }]) on 422 — returning it
// raw put an object into <Toast>'s message and crashed React with
// "Objects are not valid as a React child". extractApiError flattens every
// shape (string / FastAPI array / validate_batch array / object) to a string.
const extractErr = (e: any, fallback: string): string => {
  const s = extractApiError(e, '');
  if (s) return s;
  const errStr = e?.response?.data?.error;
  if (typeof errStr === 'string' && errStr) return errStr;
  return fallback;
};

interface UserOption { id: number; user_name: string; login_id?: string; }
interface AppOption { id: number; application_name: string; }
interface AssetOption { id: number; asset_code: string; asset_name: string; }

interface Props {
  requestId: number | null;          // null = create flow
  onClose: () => void;
  onSaved: () => void;
}


const ITGCRequestEditorModal: React.FC<Props> = ({ requestId, onClose, onSaved }) => {
  const isCreate = requestId === null;

  const reqVh = useFieldValidation();
  const { formatDate } = useEntitySettings();
  const { allowed: canModify, loading: permLoading } = usePermission('ORG_ITGC_ACCESS_REQUEST', 'view+modify');

  const [loaded, setLoaded] = useState(isCreate);
  const [existing, setExisting] = useState<AccessRequest | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const [targetUserId, setTargetUserId] = useState<number>(0);
  const [title, setTitle] = useState('');
  const [justification, setJustification] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  // Soft-inactive (is_active = 0) lines, shown read-only when the toggle is
  // flipped to INACTIVE. Loaded alongside active lines from getRequest.
  const [inactiveLines, setInactiveLines] = useState<LineDraft[]>([]);
  const [lineFilter, setLineFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');

  const [users, setUsers] = useState<UserOption[]>([]);
  const [apps, setApps] = useState<AppOption[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [busy, setBusy] = useState(false);
  // Spinner state for the active/inactive toggle (used by toggleActive).
  const [togglingActive, setTogglingActive] = useState(false);

  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [lineModalInitial, setLineModalInitial] = useState<LineDraft | null>(null);

  // Reset validation errors on modal open
  useEffect(() => { reqVh.reset(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load lookups
  useEffect(() => {
    (async () => {
      try {
        const [u, a, s] = await Promise.all([
          apiClient.get('/api/v1/usr/users', { params: { limit: 500 } }),
          apiClient.get('/api/v1/org/applications', { params: { limit: 200, status: 'ACTIVE' } }),
          apiClient.get('/api/v1/org/it-assets', { params: { limit: 200, status: 'ACTIVE' } }),
        ]);
        setUsers((u.data?.data || []).filter(isPickableUser).map((x: any) => {
          const full = [x.first_name, x.last_name].filter(Boolean).join(' ').trim();
          return { id: x.id, user_name: full || x.login_id || x.email || `User #${x.id}`, login_id: x.login_id };
        }));
        setApps((a.data?.data || []).map((x: any) => ({ id: x.id, application_name: x.application_name })));
        setAssets((s.data?.data || []).map((x: any) => ({ id: x.id, asset_code: x.asset_code, asset_name: x.asset_name })));
      } catch (e: any) {
        setToast({ type: 'error', message: e.message || 'Failed to load lookup data' });
      }
    })();
  }, []);

  // Load existing request + its lines. Active (is_active=1) lines are editable;
  // soft-inactive (is_active=0) lines are shown read-only under the toggle.
  const loadExisting = useCallback(async () => {
    if (isCreate) return;
    try {
      const req = await itgcService.getRequest(requestId!);
      setExisting(req);
      setTitle(req.title || '');
      setJustification(req.justification || '');
      const toDraft = (l: AccessLine): LineDraft => ({
        _key: l.id,
        _existingId: l.id,
        target_user_id: l.target_user_id,
        target_type: l.target_type,
        target_id: l.target_id,
        action: l.action,
        due_date: l.due_date || '',
        valid_from: l.valid_from || '',
        valid_until: l.valid_until || '',
        line_remarks: l.line_remarks || '',
        is_active: l.is_active,
      });
      const all = req.lines || [];
      const active = all.filter(l => l.is_active === 1);
      const inactive = all.filter(l => l.is_active === 0);
      setTargetUserId((active[0] || all[0])?.target_user_id || 0);
      setLines(active.map(toDraft));
      setInactiveLines(inactive.map(toDraft));
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, 'Failed to load request') });
    } finally { setLoaded(true); }
  }, [isCreate, requestId]);

  useEffect(() => { loadExisting(); }, [loadExisting]);

  const status: RequestStatus | null = existing?.status as RequestStatus || null;

  // Lock the target user as soon as ANY line exists — including unsaved draft lines
  // during create — because every line's eligibility was computed against this user.
  const userLocked = lines.length > 0;

  const openAddLine = () => {
    if (!targetUserId) {
      reqVh.setError('target_user_id', 'Pick the target user before adding lines');
      setToast({ type: 'error', message: 'Pick the target user before adding lines' });
      return;
    }
    setLineModalInitial(null);
    setLineModalOpen(true);
  };
  const openEditLine = (l: LineDraft) => { setLineModalInitial(l); setLineModalOpen(true); };
  const closeLineModal = () => { setLineModalOpen(false); setLineModalInitial(null); };

  const onLineSaved = (line: LineDraft) => {
    setLines(prev => {
      const idx = prev.findIndex(p => p._key === line._key);
      if (idx === -1) return [...prev, line];
      const next = [...prev]; next[idx] = line; return next;
    });
    closeLineModal();
  };


  // Format validation on header + line fields, plus domain checks (target user +
  // at least one line). Returns the first error message (for the toast) or null.
  const validateForm = (): string | null => {
    const fmtOk = reqVh.validateAll([
      // Title is mandatory on create — the backend's create route enforces it
      // (validate_batch required:True). Edit/update leaves it optional.
      { name: 'title', code: 109, value: title, required: isCreate },
      { name: 'justification', code: 110, value: justification },
      ...lines.flatMap((l, i) => [
        { name: `line_remarks[${i}]`, code: 110, value: l.line_remarks },
        { name: `valid_until[${i}]`, code: 305, value: l.valid_until },
      ]),
    ]);

    let domainMsg: string | null = null;
    if (!targetUserId) {
      reqVh.setError('target_user_id', 'Target user is required');
      domainMsg = domainMsg ?? 'Target user is required';
    }
    if (!lines.length) {
      reqVh.setError('lines', 'Add at least one access line');
      domainMsg = domainMsg ?? 'Add at least one access line';
    }

    if (!fmtOk) return 'Please fix the highlighted fields';
    return domainMsg;
  };

  const buildLinePayloads = (): AccessLineCreatePayload[] =>
    lines.map(l => ({
      target_user_id: targetUserId,
      target_type: l.target_type,
      target_id: l.target_id,
      action: l.action,
      due_date: l.due_date || undefined,
      valid_from: l.valid_from || undefined,
      valid_until: l.valid_until || undefined,
      line_remarks: l.line_remarks || undefined,
    }));

  const persistEditDiff = async (): Promise<boolean> => {
    if (!existing) return false;
    try {
      await itgcService.updateRequest(existing.id, {
        title: title || undefined,
        justification: justification || undefined,
      });
      // Only diff against ACTIVE existing lines — soft-inactive (0) lines are
      // managed by the Active/Inactive toggle and must not be touched here.
      const existingIds = (existing.lines || []).filter(l => l.is_active === 1).map(l => l.id);
      const keptIds = new Set<number>(lines.filter(l => l._existingId).map(l => l._existingId as number));
      for (const eid of existingIds) { if (!keptIds.has(eid)) await itgcService.deleteLine(eid); }
      for (const l of lines.filter(l => !l._existingId)) {
        await itgcService.addLine(existing.id, {
          target_user_id: targetUserId, target_type: l.target_type, target_id: l.target_id,
          action: l.action, due_date: l.due_date || undefined, valid_from: l.valid_from || undefined,
          valid_until: l.valid_until || undefined, line_remarks: l.line_remarks || undefined,
        });
      }
      return true;
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, 'Failed to save') });
      return false;
    }
  };

  const run = async (fn: () => Promise<any>, confirmMsg?: string) => {
    if (confirmMsg) {
      const ok = await swalConfirm({ title: 'Confirm', text: confirmMsg } as any);
      if (!ok) return;
    }
    setBusy(true);
    try { await fn(); onSaved(); }
    catch (e: any) { setToast({ type: 'error', message: extractErr(e, 'Action failed') }); }
    finally { setBusy(false); }
  };

  // merged: incoming + existing
  const toggleActive = async (nextStatus: 'ACTIVE' | 'INACTIVE') => {
    if (!existing) return;
    setTogglingActive(true);
    try {
      const newIsActive = nextStatus === 'ACTIVE' ? 1 : -1;
      // Call API to update is_active status with current field values
      await itgcService.updateRequest(existing.id, {
        title: title || undefined,
        justification: justification || undefined,
        is_active: newIsActive
      });
      // Reload the request to get updated data
      const updated = await itgcService.getRequest(existing.id);
      setExisting(updated);
      setToast({ type: 'success', message: nextStatus === 'ACTIVE' ? 'Request activated' : 'Request inactivated' });
    } catch (e: any) {
      setToast({ type: 'error', message: extractErr(e, 'Toggle failed') });
    } finally {
      setTogglingActive(false);
    }
  };

  // ── Actions ───────────────────────────────────────────────────────────
  const actSaveCreate = async () => {
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const err = validateForm(); if (err) { setToast({ type: 'error', message: err }); return; }
    setBusy(true);
    try {
      await itgcService.createRequest({ title: title || undefined, justification: justification || undefined, lines: buildLinePayloads() });
      onSaved();
    } catch (e: any) { setToast({ type: 'error', message: extractErr(e, 'Failed to create') }); }
    finally { setBusy(false); }
  };

  const actSaveEdit = async () => {
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const err = validateForm(); if (err) { setToast({ type: 'error', message: err }); return; }
    setBusy(true);
    try { if (await persistEditDiff()) onSaved(); }
    finally { setBusy(false); }
  };

  const actSubmit = async () => {
    if (!canModify) { setToast({ type: 'error', message: NOT_AUTHORISED_MSG }); return; }
    const err = validateForm(); if (err) { setToast({ type: 'error', message: err }); return; }
    setBusy(true);
    try {
      if (isCreate) {
        const res = await itgcService.createRequest({ title: title || undefined, justification: justification || undefined, lines: buildLinePayloads() });
        await itgcService.submitRequest(res.id);
      } else {
        if (!(await persistEditDiff())) return;
        await itgcService.submitRequest(existing!.id);
      }
      onSaved();
    } catch (e: any) { setToast({ type: 'error', message: extractErr(e, 'Submit failed') }); }
    finally { setBusy(false); }
  };

  // ── Derived ──────────────────────────────────────────────────────────
  const modalTitle = isCreate
    ? 'Create ITGC Access Request'
    : 'Edit ITGC Access Request';

  const userOptions = users.map(u => ({
    value: String(u.id),
    label: u.user_name,
  }));

  // Fields/lines are editable only while creating or while still a DRAFT. Once
  // SUBMITTED (in for approval) the request is read-only — pull it back to DRAFT
  // to change anything. Approved/rejected/fulfilled/closed are read-only too.
  const editable = isCreate || status === 'DRAFT';
  // The footer shows whenever any action is available — the Save/Submit buttons
  // when editable, or just Pull back / Cancel while SUBMITTED.
  const showFooter = editable || status === 'SUBMITTED';

  // Required fields for the footer actions: target user, title (on create), and
  // at least one line. When any are missing the Save/Submit buttons LOOK disabled
  // (dimmed via .btn-req-disabled) but stay clickable so the click shows a toast
  // naming what's missing — a native `disabled` button never fires onClick.
  const missingRequired = (): string | null => {
    if (!targetUserId) return 'Target user is required';
    if (isCreate && !title.trim()) return 'Title is required';
    if (!lines.length) return 'Add at least one access line';
    return null;
  };
  const requiredMet = missingRequired() === null;

  // Active/Inactive toggle over the request's lines. The INACTIVE view lists the
  // removed (is_active = -1) lines read-only; only the ACTIVE view is editable.
  const showingInactive = lineFilter === 'INACTIVE';
  const displayLines = showingInactive ? inactiveLines : lines;
  const rowsEditable = editable && !showingInactive;

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="lg">
        <Modal.Header>
          <span className="flex items-center gap-2">
            {modalTitle}
            {status && <Badge variant={statusVariant(status)} size="sm">{status}</Badge>}
          </span>
        </Modal.Header>

        <Modal.Body>
          {!loaded ? (
            <p className="text-sm text-gray-500">Loading request…</p>
          ) : (
            <form
              id="itgc-editor-form"
              onSubmit={e => { e.preventDefault(); }}
            >
              {/* Header fields */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <FormControl.SearchSelect
                  label="Target user"
                  required
                  value={String(targetUserId || '')}
                  onChange={value => {
                    setTargetUserId(Number(value));
                    reqVh.clearError('target_user_id');
                  }}
                  options={userOptions}
                  disabled={userLocked || !editable}
                  placeholder="— pick user —"
                  helper={editable && userLocked && lines.length > 0 ? 'Clear all lines to change the user.' : undefined}
                  error={reqVh.errors.target_user_id}
                  data-testid="SEL_itgc_editor_target_user"
                />
                <ValidatedInput
                  vh={reqVh}
                  name="title"
                  code={109}
                  required={isCreate}
                  label="Title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  disabled={!editable}
                  placeholder="e.g. New hire access — finance team"
                  data-testid="INP_itgc_editor_title"
                />
              </div>

              <div className="mt-4">
                <ValidatedTextarea
                  vh={reqVh}
                  name="justification"
                  code={110}
                  label="Justification"
                  value={justification}
                  onChange={e => setJustification(e.target.value)}
                  disabled={!editable}
                  rows={2}
                  data-testid="INP_itgc_editor_justification"
                />
              </div>

              {/* Lines section */}
              <div className="flex justify-between items-center mt-4 mb-2">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-semibold text-gray-700">Lines ({displayLines.length})</h4>
                  {!isCreate && existing && (
                    <StatusSwitch
                      value={lineFilter}
                      onChange={setLineFilter}
                      noun="lines"
                      testId="BTN_itgc_lines_toggle_active"
                    />
                  )}
                </div>
                {rowsEditable && (
                  <Button variant="fill" icon={Plus} onClick={() => { openAddLine(); reqVh.clearError('lines'); }} data-testid="BTN_itgc_editor_line_create">Create</Button>
                )}
              </div>
              <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                  <table className="table-list-table">
                    <thead>
                      <tr>
                        <th className="table-list-th table-list-cell--sno">S.No</th>
                        <th className="table-list-th">Type</th>
                        <th className="table-list-th">Target</th>
                        <th className="table-list-th">Action</th>
                        <th className="table-list-th">Due</th>
                        <th className="table-list-th">Validity</th>
                        <th className="table-list-th">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayLines.length === 0 && (
                        <tr>
                          <td colSpan={7} className="table-list-cell">
                            <NoDataFound
                              title={showingInactive ? 'No Inactive Lines' : 'No Lines'}
                              message={showingInactive
                                ? 'No removed lines.'
                                : rowsEditable
                                  ? 'No lines yet. Click "+ Create" to add one.'
                                  : 'No lines.'}
                            />
                          </td>
                        </tr>
                      )}
                      {displayLines.map((l, idx) => (
                        <tr
                          key={l._key}
                          className="table-list-row"
                          style={!editable ? { cursor: 'default' } : undefined}
                          onClick={editable ? () => openEditLine(l) : undefined}
                          data-testid={`BTN_itgc_editor_line_${l._key}_open`}
                        >
                          <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                          <td className="table-list-cell">{l.target_type}</td>
                          <td className="table-list-cell">
                            <span className="table-list-cell-title">{targetLabelFor(l, apps, assets)}</span>
                          </td>
                          <td className="table-list-cell">
                            <span className={`status-badge xs ${l.action === 'GRANT' ? 'success' : l.action === 'REVOKE' ? 'danger' : 'neutral'}`}>
                              {l.action}
                            </span>
                          </td>
                          <td className="table-list-cell">{l.due_date ? formatDate(l.due_date) : <span className="text-gray-400">—</span>}</td>
                          <td className="table-list-cell">
                            {l.action === 'GRANT' ? `${l.valid_from ? formatDate(l.valid_from) : '—'} → ${l.valid_until ? formatDate(l.valid_until) : '—'}` : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="table-list-cell table-list-cell--truncate" title={l.line_remarks}>
                            {l.line_remarks || <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </form>
          )}
        </Modal.Body>

        {showFooter && (
          <Modal.Footer>
            <div className="flex items-center gap-2 mr-auto">
              {!isCreate && status === 'SUBMITTED' && existing && (
                <Button variant="secondary-outline" icon={CornerUpLeft} onClick={() => run(() => itgcService.recallRequest(existing.id), 'Recall to DRAFT?')} disabled={busy} data-testid="BTN_itgc_editor_recall">
                  Pull back
                </Button>
              )}
              {!isCreate && (status === 'DRAFT' || status === 'SUBMITTED') && existing && (
                <Button variant="delete-outline" icon={Ban} onClick={() => run(() => itgcService.cancelRequest(existing.id), 'Cancel this request?')} disabled={busy} data-testid="BTN_itgc_editor_cancel">
                  Cancel request
                </Button>
              )}
            </div>
            {editable && (
              <>
                <Button variant="secondary-outline" icon={Save} type="button" onClick={isCreate ? actSaveCreate : actSaveEdit} disabled={permLoading || !canModify || busy} className={!requiredMet ? 'btn-req-disabled' : ''} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_itgc_editor_save">
                  {isCreate ? 'Save as Draft' : 'Save changes'}
                </Button>
                <Button variant={isCreate ? 'fill' : 'update'} icon={isCreate ? Send : Check} type="button" onClick={actSubmit} disabled={permLoading || !canModify || busy} className={!requiredMet ? 'btn-req-disabled' : ''} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_itgc_editor_submit">
                  {isCreate ? 'Submit' : 'Save & Submit'}
                </Button>
              </>
            )}
          </Modal.Footer>
        )}
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />

      {lineModalOpen && (
        <ITGCLineModal
          initial={lineModalInitial}
          initialTargetLabel={lineModalInitial ? targetLabelFor(lineModalInitial, apps, assets) : undefined}
          targetUserId={targetUserId}
          existingTargets={lines
            .filter(l => l._key !== lineModalInitial?._key)
            .map(l => ({ target_type: l.target_type, target_id: l.target_id, action: l.action }))}
          onClose={closeLineModal}
          onSave={onLineSaved}
          onToggledActive={() => { closeLineModal(); loadExisting(); }}
        />
      )}
    </>
  );
};

export default ITGCRequestEditorModal;
